/**
 * WebdeskPanel — configurazione integrazione fatturazione esterna Webdesk
 * (Wolters Kluwer). Studio + user + password, cadenza di sync, test connessione,
 * sync manuale, log delle sincronizzazioni.
 *
 * ⚠️ La password viene inviata al backend solo se modificata (campo vuoto =
 * mantieni quella esistente cifrata su server).
 */
import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Cloud, PlugZap, RefreshCw, Save, Eye, EyeOff, Clock, CheckCircle2, XCircle, Activity } from "lucide-react";
import api from "@/api";

const FREQ_OPTIONS = [
  { value: "manual", label: "Solo manuale" },
  { value: "hourly", label: "Ogni ora" },
  { value: "6h", label: "Ogni 6 ore" },
  { value: "daily", label: "Giornaliera (notte)" },
];

export default function WebdeskPanel() {
  const qc = useQueryClient();
  const [showPw, setShowPw] = useState(false);
  const [form, setForm] = useState(null);
  const [otpSessionId, setOtpSessionId] = useState(null);
  const [otpCode, setOtpCode] = useState("");
  const [otpMessage, setOtpMessage] = useState("");

  const { data: config } = useQuery({
    queryKey: ["webdesk-config"],
    queryFn: () => api.get("/webdesk/config").then((r) => r.data),
  });

  useEffect(() => {
    if (config && form === null) {
      setForm({
        enabled: config.enabled,
        studio: config.studio || "",
        username: config.username || "",
        password: "",
        sync_frequency: config.sync_frequency || "manual",
        sync_from_days: config.sync_from_days || 90,
        default_incasso_days: config.default_incasso_days || 60,
      });
    }
  }, [config, form]);

  const { data: log = [] } = useQuery({
    queryKey: ["webdesk-log"],
    queryFn: () => api.get("/webdesk/sync-log", { params: { limit: 10 } }).then((r) => r.data),
    refetchInterval: 15_000,
  });

  const save = useMutation({
    mutationFn: (payload) => api.put("/webdesk/config", payload).then((r) => r.data),
    onSuccess: () => {
      toast.success("Configurazione Webdesk salvata");
      qc.invalidateQueries({ queryKey: ["webdesk-config"] });
      setForm((f) => ({ ...f, password: "" }));
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Salvataggio fallito"),
  });

  const testConn = useMutation({
    mutationFn: () => api.post("/webdesk/test-connection").then((r) => r.data),
    onSuccess: (d) => {
      if (d.needs_otp) {
        setOtpSessionId(d.session_id || "pending");
        setOtpMessage(d.message || "");
        toast.info("Webdesk richiede un codice OTP — controlla la tua email");
      } else if (d.ok) {
        toast.success(`Test OK — ${d.message}`);
      } else {
        toast.error(`Test fallito: ${d.message}`);
      }
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore test connessione"),
  });

  const submitOtp = useMutation({
    mutationFn: () => api.post("/webdesk/otp", { code: otpCode }).then((r) => r.data),
    onSuccess: (d) => {
      if (d.ok) {
        toast.success(`Autorizzazione OK — ${d.message}`);
        setOtpSessionId(null);
        setOtpCode("");
      } else {
        toast.error(`OTP fallito: ${d.message}`);
      }
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore invio OTP"),
  });

  const syncNow = useMutation({
    mutationFn: () => api.post("/webdesk/sync").then((r) => r.data),
    onSuccess: (d) => {
      const s = d.stats || {};
      toast.success(`Sync ${d.status} — importate:${s.imported || 0} aggiornate:${s.updated || 0} errori:${s.errors || 0}`);
      qc.invalidateQueries({ queryKey: ["webdesk-config"] });
      qc.invalidateQueries({ queryKey: ["webdesk-log"] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Sync fallita"),
  });

  if (!form) return null;

  const submit = (e) => {
    e.preventDefault();
    save.mutate({
      enabled: form.enabled,
      studio: form.studio,
      username: form.username,
      password: form.password || null,
      sync_frequency: form.sync_frequency,
      sync_from_days: Number(form.sync_from_days),
      default_incasso_days: Number(form.default_incasso_days),
    });
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <section className="bg-white border border-slate-200 rounded-md p-5" data-testid="webdesk-panel">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500 flex items-center gap-2">
            <Cloud className="w-4 h-4" /> Fatturazione esterna — Webdesk
          </div>
          <p className="text-[11px] text-slate-500 mt-1 max-w-lg">
            Sincronizzazione fatture emesse con la piattaforma Webdesk (Wolters Kluwer).
            Le fatture importate compaiono in <b>Fatturato Effettivo</b> e alimentano
            il Cash Flow. La password è cifrata a riposo.
          </p>
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(e) => setField("enabled", e.target.checked)}
            data-testid="webdesk-enabled"
          />
          <span className="text-sm font-semibold">{form.enabled ? "Attiva" : "Disattivata"}</span>
        </label>
      </div>

      <form onSubmit={submit} className="grid md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="text-xs font-semibold text-slate-600 block mb-1">Studio</label>
          <input
            value={form.studio}
            onChange={(e) => setField("studio", e.target.value)}
            placeholder="es. STUDIOROSSI"
            data-testid="webdesk-studio"
            className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-600 block mb-1">Username / email</label>
          <input
            value={form.username}
            onChange={(e) => setField("username", e.target.value)}
            placeholder="utente@studio.it"
            data-testid="webdesk-username"
            className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
          />
        </div>
        <div className="md:col-span-2">
          <label className="text-xs font-semibold text-slate-600 block mb-1">
            Password {config?.password_set && <span className="text-emerald-700">(già salvata, lascia vuoto per mantenerla)</span>}
          </label>
          <div className="relative">
            <input
              type={showPw ? "text" : "password"} data-no-uppercase
              value={form.password}
              onChange={(e) => setField("password", e.target.value)}
              placeholder={config?.password_set ? "•••••••••••" : "Inserisci password"}
              data-testid="webdesk-password"
              className="w-full pr-20 px-3 py-2 border border-slate-300 rounded-sm text-sm font-mono"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              name="wd-external-secret"
              data-lpignore="true"
              data-1p-ignore="true"
              data-form-type="other"
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-2">
              {form.password.length > 0 && (
                <span className="text-[10px] font-bold text-slate-500 tabular-nums" data-testid="webdesk-password-length">
                  {form.password.length} car.
                </span>
              )}
              <button type="button" onClick={() => setShowPw((s) => !s)}
                className="text-slate-500 hover:text-slate-900">
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <p className="text-[10px] text-slate-500 mt-1">
            💡 Suggerimento: se la password contiene <code>@</code>, alcuni browser la interpretano come inizio email e rimuovono caratteri.
            Meglio <b>incollarla</b> ({navigator.userAgent.includes("Mac") ? "⌘V" : "Ctrl+V"}) da un password manager e verificare il contatore caratteri qui a destra.
          </p>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-600 block mb-1">Cadenza sync</label>
          <select
            value={form.sync_frequency}
            onChange={(e) => setField("sync_frequency", e.target.value)}
            data-testid="webdesk-frequency"
            className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white"
          >
            {FREQ_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">Finestra import (gg)</label>
            <input
              type="number" min={1} max={1825}
              value={form.sync_from_days}
              onChange={(e) => setField("sync_from_days", e.target.value)}
              data-testid="webdesk-from-days"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">Incasso previsto (gg)</label>
            <input
              type="number" min={0} max={365}
              value={form.default_incasso_days}
              onChange={(e) => setField("default_incasso_days", e.target.value)}
              data-testid="webdesk-incasso-days"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
            />
          </div>
        </div>
        <div className="md:col-span-2 flex flex-wrap gap-2 items-center pt-1">
          <button type="submit" disabled={save.isPending}
            data-testid="webdesk-save"
            className="inline-flex items-center gap-2 px-3 py-2 bg-slate-800 hover:bg-slate-900 text-white text-xs font-semibold rounded-sm disabled:opacity-60">
            <Save className="w-4 h-4" /> {save.isPending ? "Salvataggio…" : "Salva"}
          </button>
          <button type="button" onClick={() => testConn.mutate()}
            disabled={testConn.isPending || !config?.password_set}
            data-testid="webdesk-test"
            className="inline-flex items-center gap-2 px-3 py-2 border border-slate-300 hover:bg-slate-50 text-xs font-semibold rounded-sm disabled:opacity-60">
            <PlugZap className="w-4 h-4" /> {testConn.isPending ? "Test in corso…" : "Test connessione"}
          </button>
          <button type="button" onClick={() => syncNow.mutate()}
            disabled={syncNow.isPending || !form.enabled || !config?.password_set}
            data-testid="webdesk-sync"
            className="inline-flex items-center gap-2 px-3 py-2 bg-orange-600 hover:bg-orange-700 text-white text-xs font-semibold rounded-sm disabled:opacity-60">
            <RefreshCw className={`w-4 h-4 ${syncNow.isPending ? "animate-spin" : ""}`} /> {syncNow.isPending ? "Sync in corso…" : "Sincronizza ora"}
          </button>
        </div>
      </form>

      {/* Sfida OTP AccessNewLocation */}
      {otpSessionId && (
        <div className="border border-amber-300 bg-amber-50 rounded-sm p-3 mb-4" data-testid="webdesk-otp-box">
          <div className="text-sm font-bold text-amber-900 mb-1 flex items-center gap-2">
            <PlugZap className="w-4 h-4" /> Webdesk richiede autorizzazione della postazione
          </div>
          <p className="text-xs text-amber-800 mb-2">
            {otpMessage || "Controlla la tua email: Webdesk ti ha inviato un codice OTP. Inseriscilo qui sotto per autorizzare questa postazione."}
          </p>
          <div className="flex flex-wrap gap-2 items-end">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-amber-900 block mb-1">Codice OTP</label>
              <input
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\s+/g, ""))}
                placeholder="es. 123456"
                maxLength={12}
                autoFocus
                data-testid="webdesk-otp-code"
                className="px-3 py-2 border border-amber-400 rounded-sm text-sm font-mono w-40 text-center tracking-widest"
              />
            </div>
            <button
              onClick={() => submitOtp.mutate()}
              disabled={submitOtp.isPending || otpCode.length < 4}
              data-testid="webdesk-otp-submit"
              className="inline-flex items-center gap-2 px-3 py-2 bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold rounded-sm disabled:opacity-60"
            >
              {submitOtp.isPending ? "Verifica…" : "Verifica codice"}
            </button>
            <button
              onClick={() => { setOtpSessionId(null); setOtpCode(""); }}
              className="text-[11px] text-amber-800 hover:underline"
            >
              Annulla
            </button>
          </div>
          <p className="text-[10px] text-amber-700 mt-2 italic">
            Dopo la verifica ti abiliterò "ricorda per 90 giorni" automaticamente e salverò i cookie cifrati:
            le sync successive salteranno il challenge OTP.
          </p>
        </div>
      )}

      {/* Log ultime esecuzioni */}
      <div className="border-t border-slate-200 pt-3 mt-2" data-testid="webdesk-log-section">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-2">
          <Activity className="w-3.5 h-3.5" /> Ultime sincronizzazioni
        </div>
        {log.length === 0 ? (
          <div className="text-[11px] italic text-slate-400 py-2">Nessuna sincronizzazione eseguita.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="text-left p-1.5">Inizio</th>
                  <th className="text-left p-1.5">Stato</th>
                  <th className="text-left p-1.5">Origine</th>
                  <th className="text-right p-1.5">Import</th>
                  <th className="text-right p-1.5">Update</th>
                  <th className="text-right p-1.5">Errori</th>
                  <th className="text-left p-1.5">Messaggio</th>
                </tr>
              </thead>
              <tbody>
                {log.map((r) => (
                  <tr key={r.sync_id} className="border-t border-slate-100">
                    <td className="p-1.5 font-mono">{(r.started_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="p-1.5">
                      {r.status === "success" && <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-3 h-3" />OK</span>}
                      {r.status === "failed" && <span className="inline-flex items-center gap-1 text-rose-700"><XCircle className="w-3 h-3" />errore</span>}
                      {r.status === "running" && <span className="inline-flex items-center gap-1 text-amber-700"><Clock className="w-3 h-3" />in corso</span>}
                    </td>
                    <td className="p-1.5 text-slate-600">{r.triggered_by || "—"}</td>
                    <td className="p-1.5 text-right font-mono">{r.stats?.imported || 0}</td>
                    <td className="p-1.5 text-right font-mono">{r.stats?.updated || 0}</td>
                    <td className={`p-1.5 text-right font-mono ${r.stats?.errors ? "text-rose-700 font-bold" : ""}`}>{r.stats?.errors || 0}</td>
                    <td className="p-1.5 text-slate-500 truncate max-w-xs">{(r.messages || []).slice(-1)[0] || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
